import { Component, DestroyRef, ElementRef, OnInit, Renderer2, computed, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IssuesDataService } from './issues.component.service';
import { BehaviorSubject, Subscription, tap } from 'rxjs';
import { ITableColumn } from 'src/app/models/tableColumns';
import { issuesTableColumns } from 'src/app/models/tableColumns/issuesTableColumns.model';
import { IssuesTableRowViewModel } from 'src/app/models/viewModels/issuesTableRowViewModel';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { ITablePageInfo } from 'src/app/models/componentInputs/tablePageInfo.interface';
import { IssueModel, IssueTimelineEvent, PrimaryResourceObject } from 'src/app/models/api/issue.model';
import { SidePanelService } from '../../features/side-panel/services/side-panel.service';
import { FeatureFlagsService } from '../../services/infra/featureFlags/feature-flags.service';
import { IssuesFiltersModel } from 'src/app/models/filters/implementations/pageFilters/issuesFilters.model';
import { ISelectedFilters } from 'src/app/models/filters/selectedFilters.interface';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { IssuesHttpService } from 'src/app/services/httpServices/issues/issues-http.service';
import { ISelectOption } from 'src/app/models/selectOptions';
import { PaginationOperationEnum } from 'src/app/components/table/table.component';
import { ActiveFiltersHeightService } from 'src/app/services/infra/activeFiltersHeight/active-filters-height.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import { JsonHalCollection, JsonHalLinks, JsonHalCollectionPageInfo } from 'src/app/models/api/jsonhal.model';
import { IPaginationNavigationModel } from 'src/app/models/paginationNavigation.model';
import { Stack } from 'src/app/common/dataStructures/stack';
import { extractContinuationTokenFromURL } from 'src/app/common/exractContinuationTokenFromURL';
import { FiltersQueryTransformer } from 'src/app/services/filters/filtersQueryTransformer.ts/filtersQueryTransformer';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { toSentenceCase } from 'src/app/common/stringUtils';
import { Subject, merge, combineLatest, switchMap, of, forkJoin } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { AiService } from 'src/app/models/api/agentStep.model';
import { getAgentIconUrl, getAiServiceDisplayName } from 'src/app/common/aiServiceUtils';
import { PermissionsService } from 'src/app/services/infra/permissionsService/permissions.service';
import { RSA_DEMO_ISSUE_AI_SERVICES } from 'src/app/models/selectOptions/issueAiServiceSelectOptions.model';
import { ISidepanelTab } from 'src/app/components/sidepanel-tabs/sidepanel-tabs.component';
import { IToggleItem } from 'src/app/components/option-toggle/option-toggle.component';

type IssueTabType = 'all' | 'incidents' | 'exposures';

const TAB_ORDER: IssueTabType[] = ['all', 'incidents', 'exposures'];

const TAB_CLASSIFICATION_MAP: Record<IssueTabType, string | undefined> = {
  all: undefined,
  incidents: 'incident',
  exposures: 'exposure',
};

const MEETING_MODE_STORAGE_KEY = 'zenity_issues_meeting_mode';
const BOOTH_ISSUE_IDS = new Set(['rsa-demo-issue-001', 'rsa-demo-issue-002', 'rsa-demo-issue-003']);
const MEETING_ISSUE_IDS = new Set(['rsa-demo-issue-004', 'rsa-demo-issue-005', 'rsa-demo-issue-006']);
const IAM_DEMO_ISSUE_IDS = new Set([
  'issue-exposure-privileged-email-exfil',
  'issue-incident-exfil-via-agent-identity',
]);

enum ResourceIcons {
  M365_COPILOT = 'assets/images/logo/m365Copilot.svg',
  SALESFORCE_EINSTEIN = 'assets/images/logo/salesForce-icon.svg',
  GPT_ENTERPRISE = 'assets/images/logo/openAI-icon.svg',
  COPILOT_STUDIO = 'assets/images/logo/copilotStudio.svg',
  CREW_AI = 'assets/images/logo/crewai-icon.png',
  GOOGLE_GEMINI = 'assets/mockData/icons/gemini.svg',
  DEFAULT = 'assets/images/logo/agent-client-app-other.svg',
  CURSOR = 'assets/mockData/icons/cursor.svg',
  CUSTOMER_SUPPORT_ROUTER = 'assets/images/logo/azure-ai-foundry.svg',
  NEXTSOFTWAREAI_COMPANY_ASSISTANT = 'assets/images/logo/amazon-bedrock-logo.svg',
  COMPANY_LEADS_AGENT = 'assets/images/logo/vertex-ai.svg',
  PERMISSION_ASSIGNMENT_AGENT = 'assets/images/logo/serviceNow-logo.svg',
}

@Component({
  selector: 'app-issues',
  templateUrl: './issues.component.html',
  styleUrls: ['./issues.component.scss'],
})
export class IssuesComponent implements OnInit {
  public columns = signal<ITableColumn[]>(issuesTableColumns);
  public issuesCurrentPage$ = new BehaviorSubject<IssuesTableRowViewModel[]>([]);
  public isLoading$ = new BehaviorSubject<boolean>(true);
  public showNoData = false;
  public totalItems = signal(0);
  public pageInfo$: BehaviorSubject<ITablePageInfo>;
  public issuesData: IssueModel[] = [];
  public isFeatureEnabled = false;
  public sortedColumn$ = new BehaviorSubject<SortColumn | undefined>(undefined);
  public sorts?: SortColumn[];
  public pageFilters: IssuesFiltersModel;
  public isFiltersMenuOpen = signal(false);
  public isBannerVisible = signal(true);
  public hideBannerFlag = signal(false);
  public currentPage = signal(1);
  public readonly pageSize = 50;

  public currentRulesViewModel: BehaviorSubject<BehaviorSubject<IssuesTableRowViewModel>[]>;
  private _sortOnChangeSubscription$: Subscription;
  private _paginationNavigation = signal<IPaginationNavigationModel | null>(null);
  private _pageChange$: Subject<string> = new Subject<string>();
  private _rsaMockIssues: IssueModel[] | null = null;
  private _iamDemoMockIssues: IssueModel[] | null = null;
  private _isFirstPage = true;
  private _isMeetingMode = false;

  // Tab management
  public selectedTab = signal<IssueTabType>('all');
  public currentTabIndex = signal(0);
  public issueTabs = signal<ISidepanelTab[]>([
    { labelPath: 'issuesPage.allIssuesTab' },
    { labelPath: 'issuesPage.incidentsTab' },
    { labelPath: 'issuesPage.exposuresTab' },
  ]);

  public readonly classificationOptions = computed<IToggleItem[]>(() =>
    this.issueTabs().map((tab, i) => ({
      value: TAB_ORDER[i],
      labelPath: tab.labelPath,
      count: tab.value,
    }))
  );

  constructor(
    private readonly _issuesDataService: IssuesDataService,
    private readonly _featureFlagsService: FeatureFlagsService,
    private readonly _router: Router,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _sidePanel: SidePanelService,
    private readonly _storeService: StoreService,
    private readonly _issuesHttpService: IssuesHttpService,
    private readonly _activeFiltersHeightService: ActiveFiltersHeightService,
    private readonly _renderer: Renderer2,
    private readonly _el: ElementRef,
    private readonly _destroyRef: DestroyRef,
    private readonly _logger: LoggerService,
    private readonly _permissionsService: PermissionsService,
    private readonly _httpClient: HttpClient
  ) {
    this._initializePageInfo();
    this._initializeMeetingMode();
    this._initializeFilters();
    this._initializeFiltersFromQueryParams();
    this._setupHeightService();
  }

  ngOnInit(): void {
    // Check if the feature is enabled or internal access is granted
    this.isFeatureEnabled = this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.IssuesPage);
    const hasInternalAccess = this._checkInternalAccess();

    if (!this.isFeatureEnabled && !hasInternalAccess) {
      this._router.navigate(['/not-authorized']);
      return;
    }

    const hideBanner = this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.ReactHideIssuesPreviewBanner);
    this.hideBannerFlag.set(hideBanner);
    this.isBannerVisible.set(!hideBanner);

    this._subscribeToTabQueryParam();
    this._setupContinuationTokenPagination();
  }

  private _checkInternalAccess(): boolean {
    const INTERNAL_ACCESS_KEY = 'zenity_internal_access';
    return sessionStorage.getItem(INTERNAL_ACCESS_KEY) === 'true';
  }

  public fetchIssuesData(sort?: SortColumn, pageNumber?: number): void {
    this.isLoading$.next(true);

    // Update sorts if provided
    if (sort) {
      this.sorts = [sort];
    }

    // Update page number if provided
    if (pageNumber !== undefined) {
      this.currentPage.set(pageNumber);
    }

    // Get current filters
    const currentFilters = this.pageFilters ? this.pageFilters.getSelectedFilters() : undefined;

    this._issuesDataService.getIssuesData(this.sorts, currentFilters, this.currentPage()).subscribe({
      next: (response: JsonHalCollection<IssueModel>) => {
        this.issuesData = response._embedded.items.filter((issue) => issue.severity !== 'low');

        const totalCount = response.page.totalItems;

        this.totalItems.set(totalCount);
        this._updatePageInfo(totalCount);

        const viewModels = this.issuesData.map((item) => this._transformToViewModel(item));
        this.showNoData = viewModels.length === 0;
        this._loadDemoMockIssues(viewModels);
      },
      error: (error: Error) => {
        console.error('Error fetching issues data:', error);
        this.showNoData = true;
        this.isLoading$.next(false);
        this.issuesCurrentPage$.next([]);
      },
    });
  }

  public rowSelected(selectedRow: IssuesTableRowViewModel): void {
    this._sidePanel.openIssueDetails(selectedRow);
  }

  public rowUnselected() {
    this._sidePanel.close();
  }

  public onColumnSortClick(sortData: SortColumn): void {
    this.sortedColumn$.next(sortData);
    this.pageFilters.sortedColumn$.next(sortData);
  }

  public onTabChangeByValue(value: string): void {
    const tabIndex = TAB_ORDER.indexOf(value as IssueTabType);
    if (tabIndex >= 0) {
      this.onTabChange(tabIndex);
    }
  }

  public onTabChange(tabIndex: number): void {
    const newTab = TAB_ORDER[tabIndex] || 'all';
    this.selectedTab.set(newTab);
    this.currentTabIndex.set(tabIndex);

    this._router.navigate([], {
      queryParams: { tab: newTab },
      queryParamsHandling: 'merge',
    });

    this.pageFilters.setClassification(TAB_CLASSIFICATION_MAP[newTab]);
  }

  private _subscribeToTabQueryParam(): void {
    this._activatedRoute.queryParams.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((params) => {
      const tabParam = params['tab'] as IssueTabType | undefined;
      const newTab = tabParam && TAB_ORDER.includes(tabParam) ? tabParam : 'all';

      if (newTab === this.selectedTab()) {
        return;
      }

      this.selectedTab.set(newTab);
      this.currentTabIndex.set(TAB_ORDER.indexOf(newTab));
      this.pageFilters.setClassification(TAB_CLASSIFICATION_MAP[newTab]);
    });
  }

  private _getIconForResource(issue: IssueModel): string {
    // Extract aiService from primaryResource (backend now provides this)
    const aiService = issue.primaryResource?.aiService as AiService | undefined;

    // Extract primary resource type for fallback logic
    const resourceType = issue.primaryResourceType || issue.source;

    // Use centralized utility for getting icon - handles both aiService and resourceType with fallbacks
    // Default to default icon if no specific icon is found
    return getAgentIconUrl(resourceType, aiService) ?? ResourceIcons.DEFAULT;
  }

  private _extractTimelineOrigins(timeline: IssueTimelineEvent[]): string[] {
    if (!timeline || !Array.isArray(timeline)) {
      return [];
    }

    // Extract unique origins from timeline events
    const origins = new Set<string>();
    timeline.forEach((event) => {
      if (event.origin) {
        origins.add(event.origin);
      }
    });

    // Convert origins to display-friendly labels using predefined mappings
    return Array.from(origins).map((origin) => this._formatOriginLabel(origin));
  }

  private _formatOriginLabel(origin: string): string {
    switch (origin.toLowerCase()) {
      case 'runtime':
        return 'Run Time';
      case 'buildtime':
        return 'Build Time';
      default:
        return origin;
    }
  }

  private _transformToViewModel(row: IssueModel): IssuesTableRowViewModel {
    // Extract CVSS score from the scores array
    const cvssScore =
      row.cvssScores && row.cvssScores.length > 0
        ? parseFloat(row.cvssScores[0].score)
        : row.cvssScore || (row.cvss_score ? parseFloat(row.cvss_score) : 0);

    // Extract unique origins from timeline events for labels
    const timelineOrigins = this._extractTimelineOrigins(row.timeline);

    // Extract primary resource string value
    const primaryResourceStr = this._extractPrimaryResourceString(row.primaryResource);

    // Get AI service display name if available
    const aiService = row.primaryResource?.aiService as AiService | undefined;
    let primaryResourceType = row.primaryResourceType;
    if (aiService) {
      try {
        primaryResourceType = getAiServiceDisplayName(aiService);
      } catch (error) {
        console.warn('Failed to get AI service display name:', error);
      }
    }

    return {
      rowId: row.id,
      id: row.id,
      name: this._toTitleCase(row.name),
      status: row.status,
      severity: row.severity,
      cvssScore: cvssScore,
      businessCriticality: row.businessCriticality || 'medium',
      primaryResource: primaryResourceStr,
      primaryResourceIcon: this._getIconForResource(row),
      correlations: row.timeline?.length || 0,
      firstSeen: row.firstSeen ? new Date(row.firstSeen) : null,
      lastSeen: row.lastSeen ? new Date(row.lastSeen) : null,
      issue: {
        ...row,
        // Map API fields to expected model fields for backward compatibility
        integrationId: row.integration?.id || row.integrationId,
        firstActivity: row.firstSeen || row.firstActivity,
        lastActivity: row.lastSeen || row.lastActivity,
        cvssScore: cvssScore,
        labels: timelineOrigins, // Set the extracted origins as labels in the issue model
      },
      clickable: true,
      menuActions: row.menuActions || [],
      ragFiles: row.ragFiles || [],
      risk: row.risk || [],
      primaryResourceType: primaryResourceType,
      labels: timelineOrigins, // Use timeline origins as labels for the grid
    };
  }

  private _toTitleCase(value: string | undefined): string {
    if (!value) return '';
    return toSentenceCase(value);
  }

  private _extractPrimaryResourceString(primaryResource: PrimaryResourceObject | undefined): string {
    if (!primaryResource) {
      return '';
    }

    return primaryResource.displayName || primaryResource.name || primaryResource.id || '';
  }

  private _initializePageInfo() {
    this.pageInfo$ = new BehaviorSubject({
      number: 1,
      currSize: this.pageSize,
      maxSize: this.pageSize,
      totalItems: 1, // Set to 1 initially to ensure pagination renders
      totalPages: 1,
    });
  }

  private _updatePageInfo(totalItems: number): void {
    const totalPages = Math.ceil(totalItems / this.pageSize);
    const pageInfo = {
      number: this.currentPage(),
      currSize: this.pageSize,
      maxSize: this.pageSize,
      totalItems,
      totalPages,
    };
    this.pageInfo$.next(pageInfo);
  }

  public onPagination(operation: PaginationOperationEnum): void {
    if (!this.isLoading$.value) {
      const navigation = this._paginationNavigation();
      if (!navigation) return;

      if (operation == PaginationOperationEnum.next) {
        navigation.previousPage.push(navigation.currentPage);
      }

      const pageUrl = operation == PaginationOperationEnum.next ? navigation.nextPage : navigation.previousPage.pop();

      this._isFirstPage = operation !== PaginationOperationEnum.next && navigation.previousPage.size() === 0;

      const continuationToken = extractContinuationTokenFromURL(pageUrl ?? '', this._logger);
      if (continuationToken) {
        this._pageChange$.next(continuationToken);
      }
    }
  }

  private _subscribeToSortOnChange(): void {
    this._sortOnChangeSubscription$ = this.sortedColumn$.subscribe((newSort) => {
      if (newSort) {
        this.fetchIssuesData(newSort);
      }
    });
  }

  private _subscribeToFiltersChange(): void {
    if (this.pageFilters) {
      this.pageFilters.onUpdate$.subscribe(() => {
        // Reset to page 1 when filters change
        this.fetchIssuesData(undefined, 1);
      });
    }
  }

  private _initializeFilters(): void {
    const demoEnabled = this._anyDemoEnabled();
    this.pageFilters = new IssuesFiltersModel(
      this._router,
      this._storeService,
      this._issuesHttpService,
      this._permissionsService,
      demoEnabled
    );
  }

  private _initializeMeetingMode(): void {
    const isRsaDemo = this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.RsaDemoRuntimeBoundaries);
    if (!isRsaDemo && !this._isIamDemoEnabled()) {
      return;
    }

    const queryParams = this._activatedRoute.snapshot.queryParams;
    const setMeetingParam = queryParams['setMeeting']?.toLowerCase();

    if (setMeetingParam === 'true') {
      localStorage.setItem(MEETING_MODE_STORAGE_KEY, 'true');
      this._isMeetingMode = true;
    } else if (setMeetingParam === 'false') {
      localStorage.setItem(MEETING_MODE_STORAGE_KEY, 'false');
      this._isMeetingMode = false;
    } else {
      this._isMeetingMode = localStorage.getItem(MEETING_MODE_STORAGE_KEY) === 'true';
    }
  }

  private _initializeFiltersFromQueryParams(): void {
    const queryParams = this._activatedRoute.snapshot.queryParams;
    const filtersQuery = queryParams['q'];

    if (filtersQuery) {
      const parsedFilters = FiltersQueryTransformer.transformFromQuery(filtersQuery);
      if (Object.keys(parsedFilters).length > 0) {
        this.pageFilters.updateFiltersByKeyValue(parsedFilters, false);
      }
    }
  }

  public tablesFiltersChange(selectedOptions: ISelectOption[]): void {
    this.pageFilters.updateFilterFromUserSelection(undefined, selectedOptions);
  }

  public filtersOnMenuStateChange(isOpen: boolean): void {
    this.isFiltersMenuOpen.set(isOpen);
  }

  public toggleBanner(): void {
    if (this.hideBannerFlag()) {
      return;
    }
    this.isBannerVisible.update((v) => !v);
  }

  public closeBanner(): void {
    this.isBannerVisible.set(false);
  }

  private _setupHeightService(): void {
    this._activeFiltersHeightService.height$
      .pipe(
        tap((height: number) =>
          this._renderer.setStyle(this._el.nativeElement, '--applied-filters-height', `${height}px`, 1)
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  private _isRsaDemoEnabled(): boolean {
    return this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.RsaDemoRuntimeBoundaries);
  }

  private _isIamDemoEnabled(): boolean {
    return this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.SpmIamDemoAmex);
  }

  private _anyDemoEnabled(): boolean {
    return this._isRsaDemoEnabled() || this._isIamDemoEnabled();
  }

  private _emptyIssuesResponse(): JsonHalCollection<IssueModel> {
    return {
      _embedded: { items: [] },
      _links: { self: { href: '' }, next: { href: '' } },
      page: { number: 1, size: this.pageSize, totalItems: 0 },
    } as unknown as JsonHalCollection<IssueModel>;
  }

  private _setupContinuationTokenPagination(): void {
    const { onUpdate$, sortedColumn$ } = this.pageFilters;
    merge(combineLatest([onUpdate$, sortedColumn$]), this._pageChange$)
      .pipe(
        switchMap((result: [ISelectedFilters, SortColumn | undefined] | string) => {
          this.issuesCurrentPage$.next([]);
          this.isLoading$.next(true);
          if (typeof result === 'string') {
            return this._issuesHttpService.getIssuesByContinuationToken(result);
          }
          this._isFirstPage = true;
          const [filters, sort] = result;
          if (this._anyDemoEnabled() && this._isOnlyDemoServicesSelected(filters)) {
            return of(this._emptyIssuesResponse());
          }
          const stripped = this._anyDemoEnabled() ? this._stripDemoServicesFromFilters(filters) : filters;
          return this._issuesHttpService.startGetIssues(stripped, sort);
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (response: JsonHalCollection<IssueModel>) => this._handleIssuesCollectionResponse(response),
        error: (err) => this._fetchRequestOnError(err),
      });
  }

  private _handleIssuesCollectionResponse(response: JsonHalCollection<IssueModel>) {
    this._handlePaginationUrls(response._links);
    this._handlePageInfo(response.page);
    this._updateTabCountsFromFacets(response);

    const items = response._embedded.items;

    if (!items || items.length == 0) {
      if (this._anyDemoEnabled()) {
        this._loadDemoMockIssues([]);
      } else {
        this.showNoData = true;
        this.issuesCurrentPage$.next([]);
        this.isLoading$.next(false);
      }
      return;
    }

    this.showNoData = false;
    const apiViewModels = items.map((item) => this._transformToViewModel(item));

    this._loadDemoMockIssues(apiViewModels);
  }

  private _loadDemoMockIssues(apiViewModels: IssuesTableRowViewModel[]): void {
    if (!this._isFirstPage || !this._anyDemoEnabled()) {
      this.issuesCurrentPage$.next(apiViewModels);
      this.isLoading$.next(false);
      return;
    }

    const rsaCached = !this._isRsaDemoEnabled() ||
      this._rsaMockIssues !== null;
    const iamCached = !this._isIamDemoEnabled() ||
      this._iamDemoMockIssues !== null;

    if (rsaCached && iamCached) {
      this._prependMockIssues(apiViewModels);
      return;
    }

    const rsaMock$ = this._isRsaDemoEnabled() &&
      !this._rsaMockIssues
      ? this._httpClient.get<IssueModel[]>(
          'assets/mockData/rsaIssuesMockData.json'
        )
      : of(null);

    const iamMock$ = this._isIamDemoEnabled() &&
      !this._iamDemoMockIssues
      ? this._httpClient.get<IssueModel[]>(
          'assets/mockData/iamDemoIssuesMockData.json'
        )
      : of(null);

    forkJoin([rsaMock$, iamMock$]).subscribe({
      next: ([rsaData, iamData]) => {
        if (rsaData) {
          this._rsaMockIssues = rsaData;
        }
        if (iamData) {
          this._iamDemoMockIssues =
            this._applyDynamicIssueTimestamps(iamData);
        }
        this._prependMockIssues(apiViewModels);
      },
      error: () => {
        this.issuesCurrentPage$.next(apiViewModels);
        this.isLoading$.next(false);
      },
    });
  }

  private _applyDynamicIssueTimestamps(
    issues: IssueModel[]
  ): IssueModel[] {
    const now = new Date();
    const base = new Date(now);
    base.setMinutes(now.getMinutes() - 5);

    return issues.map((issue, i) => {
      const offset = i * 15 * 1000;
      const ts = new Date(base.getTime() - offset).toISOString();
      return {
        ...issue,
        firstSeen: ts,
        lastSeen: ts,
      } as IssueModel;
    });
  }

  private _prependMockIssues(apiViewModels: IssuesTableRowViewModel[]): void {
    const selectedServices = this.pageFilters?.getSelectedFilters()?.aiService ?? [];

    // Collect RSA mock issues (filtered by meeting/booth mode)
    let rsaFiltered = this._rsaMockIssues ?? [];
    const allowedIds = this._isMeetingMode ? MEETING_ISSUE_IDS : BOOTH_ISSUE_IDS;
    rsaFiltered = rsaFiltered.filter((issue) => allowedIds.has(issue.id));

    // All IAM demo mock issues (unfiltered, for tab counts)
    const iamAll = this._iamDemoMockIssues ?? [];

    // Collect IAM demo mock issues (filtered by tab)
    let iamFiltered = [...iamAll];
    const currentTab = this.selectedTab();
    if (currentTab !== 'all') {
      const tabClassification =
        currentTab === 'incidents' ? 'incident' : 'exposure';
      iamFiltered = iamFiltered.filter(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (iss) => (iss as any).classification === tabClassification
      );
    }

    // Combine: IAM demo first, then RSA
    let allMocks = [...iamFiltered, ...rsaFiltered];

    if (selectedServices.length > 0) {
      const serviceSet = new Set(selectedServices);
      allMocks = allMocks.filter((issue) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const svc = (issue as any).aiService as string | undefined;
        return svc ? serviceSet.has(svc) : false;
      });
    }
    const mockViewModels = allMocks.map((item) => this._transformMockToViewModel(item));
    const merged = [...mockViewModels, ...apiViewModels].sort(
      (a, b) => (b.lastSeen?.getTime() ?? 0) - (a.lastSeen?.getTime() ?? 0)
    );
    this.showNoData = merged.length === 0;
    this.issuesCurrentPage$.next(merged);
    this._addMockIssuesToTabCounts([...iamAll, ...rsaFiltered]);
    this.isLoading$.next(false);
  }

  private _addMockIssuesToTabCounts(mockIssues: IssueModel[]): void {
    if (mockIssues.length === 0) return;

    let mockIncidents = 0;
    let mockExposures = 0;
    mockIssues.forEach((iss) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const classification = (iss as any).classification as string;
      if (classification === 'incident') mockIncidents++;
      else if (classification === 'exposure') mockExposures++;
    });

    const currentTabs = this.issueTabs();
    if (currentTabs.length < 3) return;

    const parseCount = (val?: string): number => {
      const match = val?.match(/\((\d+)\)/);
      return match ? parseInt(match[1], 10) : 0;
    };

    const allCount = parseCount(currentTabs[0].value) + mockIncidents + mockExposures;
    const incidentCount = parseCount(currentTabs[1].value) + mockIncidents;
    const exposureCount = parseCount(currentTabs[2].value) + mockExposures;

    this.issueTabs.set([
      { labelPath: 'issuesPage.allIssuesTab', value: `(${allCount})` },
      { labelPath: 'issuesPage.incidentsTab', value: `(${incidentCount})` },
      { labelPath: 'issuesPage.exposuresTab', value: `(${exposureCount})` },
    ]);
  }

  private _transformMockToViewModel(row: IssueModel): IssuesTableRowViewModel {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const raw = row as any;
    const labels: string[] = raw.labels ?? [];
    const primaryResourceStr =
      typeof raw.primaryResource === 'string'
        ? raw.primaryResource
        : this._extractPrimaryResourceString(row.primaryResource);

    const cvssScore = raw.cvssScore ?? 0;

    return {
      rowId: row.id,
      id: row.id,
      name: this._toTitleCase(row.name),
      status: row.status,
      severity: row.severity,
      cvssScore: cvssScore,
      businessCriticality: raw.businessCriticality || 'high',
      primaryResource: primaryResourceStr,
      primaryResourceIcon: raw.primaryResourceIcon?.startsWith('assets/')
        ? raw.primaryResourceIcon
        : (raw.aiService || raw.primaryResource?.aiService)
          ? (getAgentIconUrl(undefined, raw.aiService || raw.primaryResource?.aiService) ?? ResourceIcons.DEFAULT)
          : ResourceIcons.DEFAULT,
      correlations: raw.timeline?.length || 0,
      firstSeen: row.firstSeen ? new Date(row.firstSeen) : row.firstActivity ? new Date(row.firstActivity) : null,
      lastSeen: row.lastSeen ? new Date(row.lastSeen) : row.lastActivity ? new Date(row.lastActivity) : null,
      issue: {
        ...row,
        integrationId: row.integration?.id || row.integrationId,
        firstActivity: row.firstSeen || row.firstActivity,
        lastActivity: row.lastSeen || row.lastActivity,
        cvssScore: cvssScore,
        labels: labels,
      },
      clickable: true,
      menuActions: row.menuActions || [],
      ragFiles: row.ragFiles || [],
      risk: row.risk || [],
      primaryResourceType: raw.primaryResourceType || '',
      labels: labels,
    };
  }

  private _stripRsaServicesFromFilters(filters: ISelectedFilters): ISelectedFilters {
    if (!filters.aiService?.length) return filters;
    return {
      ...filters,
      aiService: filters.aiService.filter((s) => !RSA_DEMO_ISSUE_AI_SERVICES.has(s)),
    };
  }

  private _isOnlyRsaServicesSelected(filters: ISelectedFilters): boolean {
    if (!filters.aiService?.length) return false;
    return filters.aiService.every((s) => RSA_DEMO_ISSUE_AI_SERVICES.has(s));
  }

  private _getDemoServicesSet(): Set<string> {
    const services = new Set(RSA_DEMO_ISSUE_AI_SERVICES);
    if (this._isIamDemoEnabled()) {
      services.add('langchain');
    }
    return services;
  }

  private _stripDemoServicesFromFilters(
    filters: ISelectedFilters
  ): ISelectedFilters {
    if (!filters.aiService?.length) return filters;
    const demoServices = this._getDemoServicesSet();
    return {
      ...filters,
      aiService: filters.aiService.filter(
        (s) => !demoServices.has(s)
      ),
    };
  }

  private _isOnlyDemoServicesSelected(
    filters: ISelectedFilters
  ): boolean {
    if (!filters.aiService?.length) return false;
    const demoServices = this._getDemoServicesSet();
    return filters.aiService.every((s) => demoServices.has(s));
  }

  private _updateTabCountsFromFacets(response: JsonHalCollection<IssueModel>): void {
    const counts = response.facets?.['classification'];

    // If facets are missing (e.g., pagination responses), preserve existing tab counts
    // to avoid overwriting valid counts with false zeros
    if (!counts) {
      return;
    }

    const incidentCount = counts['incident'] ?? 0;
    const exposureCount = counts['exposure'] ?? 0;
    const allCount = incidentCount + exposureCount;

    this.issueTabs.set([
      {
        labelPath: 'issuesPage.allIssuesTab',
        value: `(${allCount})`,
      },
      {
        labelPath: 'issuesPage.incidentsTab',
        value: `(${incidentCount})`,
      },
      {
        labelPath: 'issuesPage.exposuresTab',
        value: `(${exposureCount})`,
      },
    ]);
  }

  private _handlePaginationUrls(links: JsonHalLinks) {
    const currentNavigation = this._paginationNavigation();

    if (!currentNavigation) {
      this._paginationNavigation.set({
        currentPage: links.self.href,
        nextPage: links.next?.href,
        previousPage: new Stack<string>(),
      } as IPaginationNavigationModel);
    } else {
      const updatedNavigation = {
        ...currentNavigation,
        currentPage: links.self.href,
        nextPage: links.next?.href,
      };
      this._paginationNavigation.set(updatedNavigation);
    }
  }

  private _handlePageInfo(pageInfo: JsonHalCollectionPageInfo) {
    this.totalItems.set(pageInfo.totalItems);
    const tablePageInfo = {
      number: pageInfo.number,
      currSize: pageInfo.size,
      maxSize: this.pageSize,
      totalItems: pageInfo.totalItems,
      totalPages: Math.ceil(pageInfo.totalItems / this.pageSize),
    };
    this.pageInfo$.next(tablePageInfo);
  }

  private _fetchRequestOnError(error: HttpErrorResponse) {
    this.issuesCurrentPage$.next([]);
    this.isLoading$.next(false);
    this.showNoData = true;
    console.error('Error fetching issues:', error);
  }
}
